import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
    TypeOfRegistration,
    TypeOfRegistrationUpdated,
    TypeOfRegistrationOnServerCreated,
    selectTypeOfRegistrationsActionLoading,
    selectTypeOfRegistrationById,
    selectLastCreatedTypeOfRegistrationId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-type-of-registration-edit',
    templateUrl: './type-of-registration-edit.component.html',
    styleUrls: ['./type-of-registration-edit.component.scss']
})


export class TypeOfRegistrationEditComponent implements OnInit, OnDestroy {

    model: TypeOfRegistration;
    oldModel: TypeOfRegistration;

    typeOfRegistrationForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectTypeOfRegistrationsActionLoading));
        const routeSubscription = this.activatedRoute.params.subscribe(params => {
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectTypeOfRegistrationById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initTypeOfRegistration();
                    }
                });
            } else {
                this.model = new TypeOfRegistration();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initTypeOfRegistration();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Handover Standard', page: `../admin-management/linked-tables/type-of-registrations`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/type-of-registrations/edit/${id}`
                    : `../admin-management/linked-tables/type-of-registrations/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initTypeOfRegistration() {
        this.createForm();
    }


    createForm() {
        this.typeOfRegistrationForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Type Of Registration '${
                this.model.name
                }'`;
        }
        return 'New Type Of Registration';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.typeOfRegistrationForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.typeOfRegistrationForm.markAsPristine();
        this.typeOfRegistrationForm.markAsUntouched();
        this.typeOfRegistrationForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.typeOfRegistrationForm.controls;
        /** check form */
        if (this.typeOfRegistrationForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateTypeOfRegistration(this.model);
        } else {
            this.createTypeOfRegistration(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: TypeOfRegistrationModel
     */
    updateTypeOfRegistration(_model: TypeOfRegistration) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateTypeOfRegistration: Update<TypeOfRegistration> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new TypeOfRegistrationUpdated({
            partialTypeOfRegistration: updateTypeOfRegistration,
            typeOfRegistration: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: TypeOfRegistrationModel
     */
    createTypeOfRegistration(_model: TypeOfRegistration) {
        this.store.dispatch(new TypeOfRegistrationOnServerCreated({typeOfRegistration: _model}));
        this.oldModel = _model;

        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedTypeOfRegistrationId),
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
